import { useMemo, useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import { useAtomValue } from "jotai";
import { Icon } from "@iconify/react";
import { StoryItem } from "./StoryItem";
import { activeLabelsState } from "./JotaiAtoms";
import { StyledTooltip } from "./themeConfig";

type LabelDistributionProps = {
  stories: StoryItem[];
  minPercentage?: number;
  tableRef?: React.RefObject<HTMLElement>; // Reference to the table to measure cells
};

type LabelCount = {
  label: string;
  count: number;
  percentage: number;
  color: string;
};

// Simple hash function for consistent color generation
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

// Expanded subtle color palette - 45 colors to minimize collisions
const COLOR_PALETTE_LIGHT = [
  // Blues
  "#dbeafe",
  "#bfdbfe",
  "#93c5fd",
  // Greens
  "#d1fae5",
  "#a7f3d0",
  "#6ee7b7",
  // Ambers/Yellows
  "#fef3c7",
  "#fde68a",
  "#fef9c3",
  // Reds/Roses
  "#fee2e2",
  "#fecaca",
  "#ffe4e6",
  // Purples/Violets
  "#ede9fe",
  "#ddd6fe",
  "#f3e8ff",
  // Pinks
  "#fce7f3",
  "#fbcfe8",
  "#fae8ff",
  // Teals/Cyans
  "#ccfbf1",
  "#99f6e4",
  "#cffafe",
  // Oranges
  "#ffedd5",
  "#fed7aa",
  "#fef3c7",
  // Indigos
  "#e0e7ff",
  "#c7d2fe",
  "#a5b4fc",
  // Limes
  "#ecfccb",
  "#d9f99d",
  "#bef264",
  // Emeralds
  "#d1fae5",
  "#a7f3d0",
  "#6ee7b7",
  // Slates/Grays
  "#f1f5f9",
  "#e2e8f0",
  "#cbd5e1",
  // Stones
  "#f5f5f4",
  "#e7e5e4",
  "#d6d3d1",
  // Additional variety
  "#fef08a",
  "#e9d5ff",
  "#fbcfe8",
  "#fda4af",
  "#fed7aa",
  "#d8b4fe",
  "#c4b5fd",
  "#a78bfa",
  "#a5f3fc",
  "#67e8f9",
];

const COLOR_PALETTE_DARK = [
  // Blues
  "#1e3a5f",
  "#1e3a52",
  "#1a2f4a",
  // Greens
  "#1e4d3a",
  "#1a4332",
  "#16392a",
  // Ambers/Yellows
  "#4d3d1f",
  "#45361c",
  "#3d2f19",
  // Reds/Roses
  "#4d1f1f",
  "#451c1c",
  "#3d1919",
  // Purples/Violets
  "#3d2d4d",
  "#352945",
  "#2d253d",
  // Pinks
  "#4d2d3d",
  "#452935",
  "#3d252d",
  // Teals/Cyans
  "#1f4d47",
  "#1c453f",
  "#193d37",
  // Oranges
  "#4d2f1f",
  "#452a1c",
  "#3d2519",
  // Indigos
  "#2d3a5f",
  "#293352",
  "#252c45",
  // Limes
  "#3a4d1f",
  "#33451c",
  "#2c3d19",
  // Emeralds
  "#2d4d2d",
  "#294529",
  "#253d25",
  // Slates/Grays
  "#2d3748",
  "#2d3748",
  "#374151",
  // Stones
  "#3d3d3a",
  "#353532",
  "#2d2d2a",
  // Additional variety
  "#4a4d1f",
  "#3d2d5f",
  "#4d2d3d",
  "#4d1f2d",
  "#4d2f1f",
  "#3d2d4a",
  "#352d42",
  "#2d253a",
  "#1f4545",
  "#1c3d3d",
];

function getLabelColor(label: string, isDark: boolean): string {
  const hash = hashString(label);
  const palette = isDark ? COLOR_PALETTE_DARK : COLOR_PALETTE_LIGHT;
  return palette[hash % palette.length];
}

function getTextColor(isDark: boolean): string {
  return isDark ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)";
}

export function LabelBar({
  stories,
  minPercentage = 5,
  tableRef,
}: LabelDistributionProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const activeLabels = useAtomValue(activeLabelsState);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [cellGeometry, setCellGeometry] = useState<{
    left: number;
    width: number;
  } | null>(null);

  const getLabelDisplayName = (label: string): string => {
    if (activeLabels && activeLabels[label]) {
      const labelData = activeLabels[label];
      const display = labelData.display;

      // If display is a string, use it
      if (typeof display === "string") {
        return display || label;
      }

      // For icon labels or labels without display, use name if available
      if (labelData.name) {
        return labelData.name;
      }
    }
    return label;
  };

  const isIconLabel = (label: string): boolean => {
    return undefined !== getIconLabel(label);
  };

  const getIconLabel = (label: string): string | undefined => {
    if (activeLabels && activeLabels[label]) {
      const display = activeLabels[label].display;
      if (typeof display === "object") {
        return display.icon;
      }
    }
    return undefined;
  };

  useEffect(() => {
    const updateGeometry = () => {
      // Find the second cell (content cell) in the first table row
      const table = tableRef?.current || document.querySelector("table");
      if (!table) return;

      const firstRow = table.querySelector("tbody tr");
      if (!firstRow) return;

      const cells = firstRow.querySelectorAll("td");
      if (cells.length < 2) return;

      const secondCell = cells[1]; // Second cell (content cell)
      const rect = secondCell.getBoundingClientRect();
      const tableRect = table.getBoundingClientRect();

      setCellGeometry({
        left: rect.left - tableRect.left,
        width: rect.width,
      });
      setContainerWidth(rect.width);
    };

    updateGeometry();

    const resizeObserver = new ResizeObserver(updateGeometry);
    const table = tableRef?.current || document.querySelector("table");
    if (table) {
      resizeObserver.observe(table);
    }

    return () => resizeObserver.disconnect();
  }, [tableRef]);

  const distribution = useMemo(() => {
    // Count stories per label
    const labelCounts = new Map<string, number>();
    const totalStories = stories.length;

    for (const story of stories) {
      if (story.st_labels) {
        for (const label of story.st_labels) {
          labelCounts.set(label, (labelCounts.get(label) || 0) + 1);
        }
      }
    }

    if (labelCounts.size === 0 || totalStories === 0) return null;

    // Use measured width, or default to reasonable width if not yet measured
    const effectiveWidth = containerWidth > 0 ? containerWidth : 800;

    // Convert to array and calculate percentages
    const labelData: LabelCount[] = Array.from(labelCounts.entries()).map(
      ([label, count]) => ({
        label,
        count,
        percentage: (count / totalStories) * 100,
        color: getLabelColor(label, isDark),
      })
    );

    // Sort by count descending
    labelData.sort((a, b) => b.count - a.count);

    // Minimum width for a label to be readable (in pixels)
    const minReadableWidth = 40;

    // Build result by adding labels one by one until they become unreadable
    const result: LabelCount[] = [];

    for (let i = 0; i < labelData.length; i++) {
      const candidate = [...result, labelData[i]];

      // Calculate normalized percentages for this candidate set
      const totalPercentage = candidate.reduce(
        (sum, item) => sum + item.percentage,
        0
      );
      const normalized = candidate.map((item) => ({
        ...item,
        percentage: (item.percentage / totalPercentage) * 100,
      }));

      // Check if all labels would be readable with these percentages
      const allReadable = normalized.every((item) => {
        const widthInPixels = (item.percentage / 100) * effectiveWidth;
        return widthInPixels >= minReadableWidth;
      });

      if (allReadable) {
        // All labels are readable, keep this set
        result.push(labelData[i]);
      } else {
        // Adding this label makes something unreadable, stop here
        break;
      }
    }

    if (result.length === 0) return null;

    // Final normalization
    const totalShownPercentage = result.reduce(
      (sum, item) => sum + item.percentage,
      0
    );
    const normalized = result.map((item) => ({
      ...item,
      percentage: (item.percentage / totalShownPercentage) * 100,
    }));

    return normalized;
  }, [stories, isDark, containerWidth]);

  if (!distribution || distribution.length === 0) {
    return null;
  }

  if (!cellGeometry) {
    // Geometry not yet measured, don't render
    return null;
  }

  const textColor = getTextColor(isDark);

  return (
    <Box
      sx={{
        mt: 2,
        mb: 2,
        width: "100%",
        position: "relative",
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          height: "16px",
          borderRadius: "6px",
          overflow: "hidden",
          width: `${cellGeometry.width}px`,
          ml: `${cellGeometry.left}px`,
          border: isDark
            ? "1px solid rgba(255, 255, 255, 0.08)"
            : "1px solid rgba(0, 0, 0, 0.08)",
          boxShadow: isDark
            ? "0 1px 2px rgba(0, 0, 0, 0.3)"
            : "0 1px 2px rgba(0, 0, 0, 0.05)",
        }}
      >
        {distribution.map((item) => {
          const sectionWidth = (item.percentage / 100) * cellGeometry.width;
          const displayName = getLabelDisplayName(item.label);
          // Rough estimate: average character is ~7px at 11px font, plus padding
          const estimatedTextWidth = displayName.length * 7 + 8;
          const showText =
            !isIconLabel(item.label) && sectionWidth >= estimatedTextWidth;

          return (
            <StyledTooltip
              key={item.label}
              title={
                isIconLabel(item.label) ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Icon
                      icon={getIconLabel(item.label)!}
                      style={{ fontSize: "14px" }}
                    />
                    <span>
                      {item.count} stories ({item.percentage.toFixed(1)}%)
                    </span>
                  </Box>
                ) : (
                  `${displayName}: ${
                    item.count
                  } stories (${item.percentage.toFixed(1)}%)`
                )
              }
              arrow
              enterDelay={300}
            >
              <Box
                sx={{
                  backgroundColor: item.color,
                  width: `${item.percentage}%`,
                  cursor: "default",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 0.3,
                  fontSize: "11px",
                  lineHeight: "16px",
                  fontWeight: 500,
                  color: textColor,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  transition: "opacity 0.15s",
                  "&:hover": {
                    opacity: 0.75,
                  },
                }}
              >
                {isIconLabel(item.label) ? (
                  <Icon
                    icon={getIconLabel(item.label)!}
                    style={{ fontSize: "11px" }}
                  />
                ) : (
                  showText && displayName
                )}
              </Box>
            </StyledTooltip>
          );
        })}
      </Box>
    </Box>
  );
}
